import { Archive, ArrowLeft } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../discovery.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'

import { ActivityGrid } from '../components/activity-grid'
import { EmptyState } from '../components/empty-state'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { RefreshStatus } from '../components/refresh-status'
import { useActivityList } from '../hooks/use-activity-list'
import { useIsDesktop } from '../hooks/use-is-desktop'

/**
 * `/history`: past joined/attended activities (UI_PARITY §3.8). A single bar
 * with back + title replaces the old logo/double-title stack on mobile.
 */
export function HistoryScreen() {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const { items, phase, refreshing, error, refresh } = useActivityList('history')

  return (
    <section aria-labelledby="history-title" className="content-width pt-[var(--safe-top)] pb-10">
      <header className="flex items-center gap-2 py-3">
        <Link
          to="/saved"
          className="grid size-11 shrink-0 place-items-center rounded-full text-deep hover:bg-mint"
          aria-label={t('common.back')}
        >
          <ArrowLeft size={22} weight="bold" aria-hidden="true" />
        </Link>
        <h1 id="history-title" className="text-[22px] font-extrabold">
          {t('screens.history.title')}
        </h1>
        {isDesktop ? (
          <div className="ml-auto">
            <RefreshButton onRefresh={refresh} refreshing={refreshing} />
          </div>
        ) : null}
      </header>

      <PullToRefresh
        className="mt-4"
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <RefreshStatus visible={refreshing} />
        {phase === 'loading' ? (
          <LoadingState variant="cards" labelKey="discovery.history.loading" />
        ) : phase === 'error' ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : items.length === 0 ? (
          <EmptyState
            compact
            icon={<Archive size={28} weight="bold" aria-hidden="true" />}
            titleKey="discovery.history.empty.title"
            bodyKey="discovery.history.empty.body"
          />
        ) : (
          <ActivityGrid activities={items} showSave={false} />
        )}
      </PullToRefresh>
    </section>
  )
}
